import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import {
    AutocompleteFunction,
    BaseAdvancedSearchSandbox,
    BaseFieldDefinition,
    BaseNestedFieldDefinition,
    ExtendedBaseProfileField,
    FieldDefinitionValue,
    FormSelectOption,
    formSelectOptionSorter,
    SearchParams
} from '@stylehaven/components';
import { AutocompleteService } from '../../services/autocomplete/autocomplete.service';
import { FieldDefinitionService } from '../../services/field-definition/field-definition.service';
import { ProfilesService } from '../../services/profiles/profiles.service';

@Injectable({ providedIn: 'root' })
export class AdvancedSearchSandboxService extends BaseAdvancedSearchSandbox<BaseNestedFieldDefinition> {
    protected fieldDefinitionService: FieldDefinitionService = inject(FieldDefinitionService);

    constructor(
        private profileService: ProfilesService,
        private readonly autocompleteService: AutocompleteService
    ) {
        super();
    }

    profileFieldsToTypeSelectOptions(
        profileFields: ExtendedBaseProfileField<BaseNestedFieldDefinition>[]
    ): FormSelectOption<string>[] {
        return profileFields.map(super.profileFieldToTypeSelectOption).sort(formSelectOptionSorter);
    }

    getProfileFieldsStream(tag?: string): Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>[]> {
        return this.profileService.getAdvancedSearchProfileFields(tag);
    }

    protected getTranslationOrDefault(key: string, fallback: string): string {
        const translated = this.translateService.instant(key);
        return translated === key ? fallback : translated;
    }

    getAutocompleteFct(dottedKey: string): AutocompleteFunction | undefined {
        return this.autocompleteService.getFieldAutocompleteFunction(dottedKey).bind(this.autocompleteService);
    }

    protected getProfileFieldLabel(profileField: ExtendedBaseProfileField<BaseNestedFieldDefinition>): string {
        return profileField.DisplayLabel;
    }

    protected multiSelectLabelFactory: (dottedKey: string, label: string) => string = (
        dottedKey: string,
        label: string
    ): string => this.getTranslationOrDefault(`overrides.${dottedKey}`, label);

    protected getFieldDefinitionValues(
        fieldDefinition: BaseFieldDefinition,
        searchTerm?: string
    ): Observable<FieldDefinitionValue[]> {
        const searchParams: SearchParams = {};
        if (searchTerm !== undefined) {
            searchParams.Label = `*${searchTerm}*`;
        }
        return this.fieldDefinitionService.getFieldDefinitionValues(fieldDefinition.DottedKey, searchParams);
    }
}
